import { t } from '@/i18n'

const MAINTENANCE_MESSAGE_RE = /väliaikaisesti estetty/i

function readErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || fallback
}

/** Walk `Error.cause` for the first axios-like failure (has `response` or `request`). */
function findAxiosError(err) {
  let current = err
  for (let i = 0; i < 5 && current; i += 1) {
    if (current.response || current.request) return current
    current = current.cause
  }
  return null
}

function failureMessages(axiosErr, err) {
  return [err?.message, axiosErr?.response?.data?.message, axiosErr?.response?.data?.error]
    .filter(Boolean)
    .join(' ')
}

/**
 * Site-wide maintenance from `protect` / auth validate — 503 plus the
 * Finnish lockout phrase. Other 503s (e.g. invoice service) must not match.
 */
function isSiteMaintenanceResponse(error) {
  if (error?.response?.status !== 503) return false
  return MAINTENANCE_MESSAGE_RE.test(
    error.response?.data?.message || error.response?.data?.error || '',
  )
}

/** Auth-form mapper: site-maintenance message, or any 503 from login/forgot. */
function isMaintenanceFailure(axiosErr, err) {
  if (isSiteMaintenanceResponse(axiosErr)) return true
  if (MAINTENANCE_MESSAGE_RE.test(failureMessages(axiosErr, err))) return true
  return axiosErr?.response?.status === 503
}

function mapLoginError(err) {
  const axiosErr = findAxiosError(err)
  if (err?.cause && axiosErr && !axiosErr.response) return t('common.login.errors.network')
  if (!err?.cause && axiosErr && !axiosErr.response) return t('common.login.errors.network')

  if (isMaintenanceFailure(axiosErr, err)) return 'maintenance'

  switch (axiosErr?.response?.status) {
    case 400:
    case 401:
    case 403:
      return t('common.login.errors.invalidCredentials')
    case 429:
      return t('common.login.errors.rateLimited')
    case 500:
    case 502:
    case 504:
      return t('common.login.errors.serverError')
    default:
      return t('common.login.errors.generic')
  }
}

function mapForgotPasswordError(err) {
  const axiosErr = findAxiosError(err)
  if (err?.cause && axiosErr && !axiosErr.response) return t('common.forgotPassword.errors.network')
  if (!err?.cause && axiosErr && !axiosErr.response) return t('common.forgotPassword.errors.network')

  if (isMaintenanceFailure(axiosErr, err)) return 'maintenance'

  switch (axiosErr?.response?.status) {
    case 429:
      return t('common.forgotPassword.errors.rateLimited')
    case 500:
    case 502:
    case 504:
      return t('common.forgotPassword.errors.serverError')
    default:
      return t('common.forgotPassword.errors.generic')
  }
}

/**
 * Body for POST list/search pickers — same fields as {@link buildSearchParams}
 * but as a JSON object (avoids URI length limits for large `accountIds`).
 */
function buildSearchBody({
  search = '',
  page,
  pageSize,
  accountIds,
  accountId,
  groupId,
  ticketGroupId,
  ticketGroupIds,
  includeAccess,
} = {}) {
  const body = {}
  if (search) body.search = search
  if (page != null) body.page = page
  if (pageSize != null) body.itemsPerPage = pageSize
  if (groupId != null) body.groupId = groupId
  if (accountId != null) body.accountId = accountId
  if (includeAccess) body.includeAccess = true
  if (accountIds?.length) body.accountIds = accountIds.map(String).filter(Boolean)
  const groups = [
    ...(ticketGroupId != null && ticketGroupId !== '' ? [String(ticketGroupId)] : []),
    ...(ticketGroupIds ?? []).map(String).filter(Boolean),
  ]
  if (groups.length) body.ticketGroupIds = [...new Set(groups)]
  return body
}

function buildFormData(fields) {
  const formData = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    if (Array.isArray(value)) {
      for (const item of value) formData.append(key, item)
    } else {
      formData.append(key, value)
    }
  }
  return formData
}

function buildQueryParams(fields) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) {
    if (value == null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item != null && item !== '') params.append(key, item)
      }
    } else {
      params.set(key, String(value))
    }
  }
  return params
}

function buildSearchParams({
  search = '',
  page,
  pageSize,
  accountIds,
  accountId,
  groupId,
  ticketGroupId,
  ticketGroupIds,
  includeAccess,
} = {}) {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (page != null) params.set('page', String(page))
  if (pageSize != null) params.set('itemsPerPage', String(pageSize))
  if (groupId != null) params.set('groupId', groupId)
  if (accountId != null) params.set('accountId', accountId)
  if (includeAccess) params.set('includeAccess', 'true')
  for (const id of accountIds ?? []) {
    if (id != null && id !== '') params.append('accountIds', String(id))
  }
  if (ticketGroupId != null && ticketGroupId !== '') params.append('ticketGroupIds', String(ticketGroupId))
  for (const id of ticketGroupIds ?? []) {
    if (id != null && id !== '') params.append('ticketGroupIds', String(id))
  }
  return params
}

class HttpUtility {
  error = {
    /**
     * Pull a user-facing error message out of an axios error, falling back
     * to the caller's localized default when the server did not provide one.
     * Server payloads use `{ message }` for normal failures and `{ error }`
     * for validation/format issues — both are checked before the fallback.
     *
     * @param {unknown} error - Thrown error (typically axios).
     * @param {string} fallback - Localized message when nothing is on the response.
     * @returns {string}
     */
    message(error, fallback) {
      return readErrorMessage(error, fallback)
    },

    /**
     * Map a thrown login error to a localized inline message for the login
     * form, or `'maintenance'` when the auth server returns HTTP 503.
     * Expects the controller's wrapped `Error` (`cause` is the axios failure).
     *
     * @param {unknown} err
     * @returns {string}
     */
    login(err) {
      return mapLoginError(err)
    },

    /**
     * Map a thrown forgot-password error to a localized inline message, or
     * `'maintenance'` when the auth server returns HTTP 503. Expects the
     * controller's wrapped `Error` (`cause` is the axios failure).
     *
     * @param {unknown} err
     * @returns {string}
     */
    forgot(err) {
      return mapForgotPasswordError(err)
    },

    /**
     * Site-wide maintenance lockout from API `protect` (503 + Finnish phrase).
     * Not every 503 — invoice/service downtime must not kick users out.
     *
     * @param {unknown} error - Axios-like failure.
     * @returns {boolean}
     */
    isMaintenance(error) {
      return isSiteMaintenanceResponse(error)
    },
  }

  /**
   * Build `FormData` for multipart requests. Array values append once per
   * entry under the same key (e.g. `files: File[]`); other values append as-is.
   *
   * @param {Record<string, string|Blob|File|(string|Blob|File)[]>} fields
   * @returns {FormData}
   */
  form(fields) {
    return buildFormData(fields)
  }

  /**
   * Generic query string builder. Skips `null`, `undefined`, and `''`;
   * array values append once per entry under the same key.
   *
   * @param {Record<string, string|number|boolean|(string|number|boolean)[]>} fields
   * @returns {URLSearchParams}
   */
  query(fields) {
    return buildQueryParams(fields)
  }

  /**
   * `URLSearchParams` for paginated scope-aware search (`search`, `page`,
   * `itemsPerPage`, optional `groupId` / `accountId` / `accountIds` /
   * `ticketGroupIds`). Only present keys are written. Account and ticket-group
   * ids serialize as repeated `accountIds` / `ticketGroupIds` params (Express
   * parses these into arrays); pass arrays (`[]` when none), or a single
   * `ticketGroupId` which is appended to `ticketGroupIds`.
   *
   * @param {object} [options]
   * @param {string} [options.search='']
   * @param {number} [options.page]
   * @param {number} [options.pageSize] - Sent as `itemsPerPage`.
   * @param {string[]} [options.accountIds]
   * @param {string} [options.accountId]
   * @param {string} [options.groupId]
   * @param {string} [options.ticketGroupId] - Convenience single id, appended to `ticketGroupIds`.
   * @param {string[]} [options.ticketGroupIds]
   * @returns {URLSearchParams}
   */
  search(options) {
    return buildSearchParams(options)
  }

  /**
   * JSON body for POST search/picker endpoints (large `accountIds` lists).
   * Same option shape as {@link HttpUtility#search}.
   *
   * @param {Parameters<typeof buildSearchBody>[0]} [options]
   * @returns {Record<string, unknown>}
   */
  searchBody(options) {
    return buildSearchBody(options)
  }
}

export default new HttpUtility()
